import { ApplicationState } from 'app/types-store';
import { ActionDispatching } from 'app/types-shared';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { ErrorNotification } from 'app/infrastructure/notifications/user-messages';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { filterConnectedCases } from './mapping/filter-connected-cases';
import { mapBowtieData, mapCaseData } from './mapping/map-bowtie';
import { mapIncludeConnectedCases } from './mapping/map-include-connected-cases';
import { getInspectionEvaluations, mapChecklistEvaluation } from './mapping/map-inspection';
import { api } from './api';
import {
  ClearBowtieData,
  RequestBowtie, ReceiveBowtie, FailBowtie,
  RequestDraft, ReceiveDraft, FailDraft, CleanData,
  RequestConnectDraft, ReceiveConnectDraft, FailConnectDraft,
  RequestCase, ReceiveCase, FailCase,
  RequestConnectedCases, ReceiveConnectedCases, FailConnectedCases, RequestBowtieFavourite, ReceiveBowtieFavourite, IncrementRequestId
} from './action-types';
import { BowtieLayout } from './types';

export const actionCreators = {

  loadBowtie: (
    bowtieLayout: BowtieLayout,
    caseNumber: number,
    connectedDraftId: number
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      const draftMode = isDefinedIncludingZero(connectedDraftId);

      dispatch(new ClearBowtieData());
      dispatch(new RequestBowtie(caseNumber, connectedDraftId));

      if (draftMode) {
        await dispatch(actionCreators.loadDraft(connectedDraftId));
      }

      try {
        const bowtieFavouritePromise = dispatch(actionCreators.createBowtieFavouriteIfNeeded());

        const layoutData = bowtieLayout != null
          ? bowtieLayout
          : await api.loadLayout();

        const {
          draftData
        } = getState().bowtie;

        const includeConnectedCases = !draftMode || mapIncludeConnectedCases(layoutData);

        const draftOriginalCase = draftMode && isDefinedIncludingZero(draftData?.caseNumber)
          ? draftData.caseNumber : undefined;

        await bowtieFavouritePromise;
        const {
          bowtieFavouriteId
        } = getState().bowtie;

        const bowtieData = await api.loadBowtie(caseNumber, includeConnectedCases, draftOriginalCase, bowtieFavouriteId);

        const caseDataModel = mapBowtieData(bowtieData.caseDataModel);
        const evaluations = getInspectionEvaluations(caseDataModel.cases);

        if (caseDataModel.cases.length === 0) {
          dispatch(new FailBowtie(caseNumber, connectedDraftId));
        } else {
          dispatch(new ReceiveBowtie(caseNumber, connectedDraftId, caseDataModel, layoutData, bowtieData.caseTemplate, evaluations));
        }

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailBowtie(caseNumber, connectedDraftId));
      }
    },

  loadDraft: (draftId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      const bowtieState = getState().bowtie;
      const requestId = (bowtieState.draftRequestId || 0) + 1;

      dispatch(new RequestDraft(requestId));

      try {
        const [
          draftData,
          connectionData
        ] = await Promise.all([
          api.loadDraft(draftId),
          api.loadDraftConnectionOptions(draftId)
        ]);

        const connectionTypes = connectionData && connectionData.rootCodes &&
          connectionData.rootCodes.map(codeItemToStoreValue);

        dispatch(new ReceiveDraft(requestId, mapCaseData(draftData), connectionTypes));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailDraft(requestId));
      }
    },

  loadEvaluation: (caseNumber: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestCase(caseNumber));

      try {

        const {
          bowtieFavouriteId
        } = getState().bowtie;

        const evaluations = await api.loadEvaluation(caseNumber, bowtieFavouriteId);
        const evaluation = evaluations.find(e => e.caseId === caseNumber);

        dispatch(new ReceiveCase(caseNumber, mapChecklistEvaluation(evaluation)));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailCase(caseNumber));
      }
    },

  loadConnectedCases: (caseNumber: number, connectionType: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: () => ApplicationState) => {

      dispatch(new RequestConnectedCases(caseNumber, connectionType));

      try {

        const {
          bowtieFavouriteId
        } = getState().bowtie;

        const connectedCases = await api.loadConnectedCases([caseNumber], [connectionType], bowtieFavouriteId);

        dispatch(new ReceiveConnectedCases(caseNumber, connectionType,
          filterConnectedCases(caseNumber, connectedCases, getState().bowtie.caseDataModel)
        ));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailConnectedCases(caseNumber, connectionType));
      }
    },

  clean: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new CleanData());
    },

  addConnection: (draftId: number, caseNumber: number, connectionTypeId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        dispatch(new RequestConnectDraft(caseNumber));

        await api.addDraftConnection(draftId, caseNumber, connectionTypeId);

        dispatch(new ReceiveConnectDraft(caseNumber));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailConnectDraft(caseNumber));
      }

      dispatch(actionCreators.loadDraft(draftId));
    },

  removeConnection: (draftId: number, caseNumber: number, connectionTypeId: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        dispatch(new RequestConnectDraft(caseNumber));

        await api.removeDraftConnection(draftId, caseNumber, connectionTypeId);

        dispatch(new ReceiveConnectDraft(caseNumber));

      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailConnectDraft(caseNumber));
      }

      dispatch(actionCreators.loadDraft(draftId));
    },

  createBowtieFavouriteIfNeeded: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      try {
        const {
          bowtieFavouriteId
        } = getState().bowtie;

        if (!bowtieFavouriteId) {
          dispatch(new RequestBowtieFavourite());
          const utcOffset = getState().app.settings.utcOffset;
          const newBowtieFavouriteId = await api.createBowtieFavourite({ utcOffset });
          dispatch(new ReceiveBowtieFavourite(newBowtieFavouriteId));
        }
      }
      catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
      }
    },

  incrementRequestId: (): ActionDispatching<any, any> =>
    (dispatch: any) => {
      dispatch(new IncrementRequestId());
    }
};
